<template>
  <b-row>
    <b-col cols="12">
      <!-- Success Message -->
      <b-row v-if="successMessage" class="mb-16">
        <b-col>
          <b-alert variant="success" show dismissible @dismissed="clearSuccessMessage">
            {{ successMessage }}
          </b-alert>
        </b-col>
      </b-row>

      <!-- Error Message -->
      <b-row v-if="errorMessage" class="mb-16">
        <b-col>
          <b-alert variant="danger" show dismissible @dismissed="clearErrorMessage">
            {{ errorMessage }}
          </b-alert>
        </b-col>
      </b-row>
    </b-col>

    <b-col cols="12">
      <h2>Change Password</h2>
      <p class="hp-p1-body mb-0">
        Set a unique password to protect your account.
      </p>
    </b-col>

    <div class="divider mx-16 border-black-40 hp-border-color-dark-80"></div>

    <b-col cols="12">
      <b-row>
        <b-col cols="12" sm="8" md="7" xl="5" class="col-xxxl-3">
          <b-form @submit.prevent="handleChangePassword">
            <!-- Old Password -->
            <b-form-group label="Old Password :" label-for="oldPassword" class="mb-24">
              <b-form-input
                id="oldPassword"
                type="password"
                placeholder="Old Password"
                v-model="oldPassword"
                minlength="8"
                required
              ></b-form-input>
              <small class="text-danger" v-if="oldPassword.length > 0 && oldPassword.length < 8">
                Password must be at least 8 characters long.
              </small>
            </b-form-group>

            <!-- New Password -->
            <b-form-group label="New Password :" label-for="newPassword" class="mb-24">
              <b-form-input
                id="newPassword"
                type="password"
                placeholder="New Password"
                v-model="newPassword"
                maxlength="10"
                required
              ></b-form-input>
              <small class="text-danger" v-if="newPassword.length > 10">
                Password must not exceed 10 characters.
              </small>
            </b-form-group>

            <!-- Confirm Password -->
            <b-form-group label="Confirm Password :" label-for="confirmPassword" class="mb-24">
              <b-form-input
                id="confirmPassword"
                type="password"
                placeholder="Re-enter new password"
                v-model="confirmPassword"
                :state="confirmPasswordState"
                maxlength="10"
                required
              ></b-form-input>
              <small class="text-danger" v-if="confirmPassword && confirmPassword !== newPassword">
                Confirm Password must match the new password.
              </small>
            </b-form-group>

            <b-button
              type="submit"
              variant="primary"
              class="w-100"
              :disabled="!formValid"
            >
              Change Password
            </b-button>
          </b-form>
        </b-col>
         <!-- Divider line -->
        <b-col cols="12" md="1" class="d-flex align-items-center justify-content-center">
          <div style="width:1px; height:100%; background:#ddd;"></div>
        </b-col>
          <!-- Profile Avatar -->
          <b-col cols="12" md="6">
            <b-form-group
              label="Profile Avatar:"
              description="Recommended size: 300x300px (JPG/PNG)"
              label-class="font-weight-bold"
            >
              <div
                class="border rounded p-3 text-center"
                style="cursor: pointer; background: #f9f9f9;"
                @drop.prevent="onDrop"
                @dragover.prevent
                @click="$refs.fileInput.click()"
              >
                <div v-if="loading" class="p-3">
                  <b-spinner small></b-spinner>
                  <p class="mb-0">Uploading image...</p>
                </div>
                <div v-else-if="imagePreview">
                  <img
                    :src="imagePreview"
                    alt="Preview"
                    class="img-thumbnail"
                    style="max-height: 120px;"
                  />
                </div>
                <div v-else>
                  <i class="fas fa-cloud-upload-alt fa-2x text-muted"></i>
                  <p class="mb-0">Drag & Drop or Click to Upload</p>
                </div>

                <input
                  ref="fileInput"
                  type="file"
                  accept="image/png, image/jpeg"
                  class="d-none"
                  @change="onFileChange"
                />
              </div>
              <small class="text-muted">
                Only JPG/PNG, Max size: 5MB
              </small>
              <small class="text-danger" v-if="imageError">
                {{ imageError }}
              </small>

            </b-form-group>
              <b-button
                @click="updateProfilePicture"
                variant="primary"
                class="w-100 d-flex align-items-center justify-content-center"
                :disabled="loading"
              >
                <b-spinner
                  small
                  type="border"
                  v-if="loading"
                  class="mr-2"
                ></b-spinner>
                {{ loading ? "Uploading..." : "Update Profile Picture" }}
              </b-button>
          </b-col>
      </b-row>
    </b-col>
  </b-row>
</template>

<script>
import axios from "axios";
import { BRow, BCol, BButton, BForm, BFormGroup, BFormInput, BAlert, BFormInvalidFeedback, BSpinner,} from "bootstrap-vue";

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BAlert,
    BFormInvalidFeedback,
    BSpinner,
  },
  data() {
    return {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
      successMessage: "",
      errorMessage: "",
      image: null,
      imagePreview: null,
      imageError: "",
      loading: false,
    };
  },
mounted() {
    this.fetchUserProfile(); // load avatar on mount
  },
  computed: {
    confirmPasswordState() {
      if (!this.confirmPassword) return null;
      return this.confirmPassword === this.newPassword;
    },
    formValid() {
      return (
        this.oldPassword.length >= 8 &&
        this.newPassword.length > 0 &&
        this.newPassword.length <= 10 &&
        this.confirmPassword === this.newPassword
      );
    },
  },

  methods: {
    async fetchUserProfile() {
      try {
        const token = localStorage.getItem("token");
        const userType = localStorage.getItem("type");

        // Map user type to profile endpoint
        const apiEndpoint =
          userType === "super_admin"
            ? "https://wossdb.wossautoid.com/api/profile"
            : userType === "police"
            ? "https://wossdb.wossautoid.com/api/profilePolice"
            : userType === "manager"
            ? "https://wossdb.wossautoid.com/api/profileManager"
            : userType === "agent"
            ? "https://wossdb.wossautoid.com/api/profileAgent"
            : null;

        if (!apiEndpoint) {
          this.errorMessage = "Invalid user type. Cannot fetch profile.";
          return;
        }

        const response = await axios.get(apiEndpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data?.data?.image) {
          this.imagePreview = `https://wossdb.wossautoid.com/${response.data.data.image}`;
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    },

    async updateProfilePicture() {
      if (!this.image) {
        this.errorMessage = "Please select an image.";
        return;
      }
      this.loading = true; 
      try {
        const formData = new FormData();
        formData.append("image", this.image);

        const token = localStorage.getItem("token");
        const userType = localStorage.getItem("type");

        // Map user type to correct endpoint
        const apiEndpoint =
          userType === "super_admin"
            ? "https://wossdb.wossautoid.com/api/update/profile"
            : userType === "police"
            ? "https://wossdb.wossautoid.com/api/update/profilePolice"
            : userType === "manager"
            ? "https://wossdb.wossautoid.com/api/update/profile/manager"
            : userType === "agent"
            ? "https://wossdb.wossautoid.com/api/update/profile/agent"
            : null;

        if (!apiEndpoint) {
          this.errorMessage = "Invalid user type. Cannot update profile.";
          return;
        }

        const response = await axios.post(apiEndpoint, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success) {
          const updatedUser = response.data.data?.user || response.data.data;

          // Update localStorage
          if (updatedUser) {
            localStorage.setItem("user", JSON.stringify(updatedUser));
            localStorage.setItem("imgVersion", Date.now()); // bust cache
          }

          // Trigger reload in Header
          this.$root.$emit("profile-updated");

          // UI feedback
          this.successMessage =
            response.data.message || "Profile picture updated!";
          this.$bvToast.toast(this.successMessage, {
            title: "Success",
            variant: "primary",
            solid: true,
          });

          // Update preview immediately
          if (updatedUser?.image) {
            this.imagePreview =
              `https://wossdb.wossautoid.com/${updatedUser.image}?t=${Date.now()}`;
          }
        } else {
          this.errorMessage = response.data.message || "Update failed.";
          this.$bvToast.toast(this.errorMessage, {
            title: "Error",
            variant: "danger",
            solid: true,
          });
        }
      } catch (error) {
        console.error("Error updating picture:", error);
        this.errorMessage =
          error.response?.data?.message || "Failed to update profile picture.";
      }finally {
        this.loading = false;
      }
    },

    handleChangePassword() {
      const userType = localStorage.getItem("type");

      const apiEndpoint =
      userType === "super_admin"
        ? "https://wossdb.wossautoid.com/api/PasswordChanged"
        : userType === "police"
        ? "https://wossdb.wossautoid.com/api/PasswordChangedPolice"
        : userType === "manager"
        ? "https://wossdb.wossautoid.com/api/PasswordChangedManager"
        : userType === "agent"
        ? "https://wossdb.wossautoid.com/api/PasswordChangedAgent"
        : null;

      if (!apiEndpoint) {
        this.errorMessage = "Invalid user type. Cannot change password.";
        return;
      }

      axios
        .post(apiEndpoint, {
          old_password: this.oldPassword,
          password: this.newPassword,
        },{
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        })
        .then((response) => {
          if (response.data.success) {
            this.successMessage = response.data.message || "Password changed successfully!";
            this.clearInputFields();
          } else {
            this.errorMessage = response.data.message || "Password change failed.";
          }
        })
        .catch((error) => {
          console.error(error);
          this.errorMessage =
            error.response?.data?.message || "Failed to change password.";
        });
    },

    clearInputFields() {
      this.oldPassword = "";
      this.newPassword = "";
      this.confirmPassword = "";
    },

    clearSuccessMessage() {
      this.successMessage = "";
    },

    clearErrorMessage() {
      this.errorMessage = "";
    },
    async onFileChange(event) {
      const file = event.target.files[0];
      await this.handleFile(file);
    },
    async onDrop(e) {
      const file = e.dataTransfer.files[0];
      await this.handleFile(file);
    },
    async  handleFile(file) {
      if (!file) return;

      if (!["image/jpeg", "image/png"].includes(file.type)) {
        this.imageError = "Only JPG/PNG allowed.";
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        this.imageError = "Max size is 5MB.";
        return;
      }

      this.image = file;
       if (this.imagePreview) {
          URL.revokeObjectURL(this.imagePreview);
        }
      this.imagePreview = URL.createObjectURL(file);
      this.imageError = "";
  },
  }
};
</script>
